import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import {
  FileText, FileSpreadsheet, Users, Layers, User, Building2,
  CalendarDays, Check, Download, ClipboardList, AlertTriangle,
} from 'lucide-react';
import Modal from './Modal';
import DateInput from './DateInput';
import { useLanguage } from '../context/LanguageContext';

// Un mosaico seleccionable. Los tres grupos de opciones del modal (formato, facilidad, filtro de
// empleados, tipo de reporte) son el mismo control: se ve cual esta elegido sin tener que leer.
function Choice({ active, onClick, icon, label, sub, children }) {
  return (
    <div>
      <button type="button" className={`exp-choice${active ? ' is-active' : ''}`} onClick={onClick}>
        {icon && <span className="exp-choice-icon">{icon}</span>}
        <span style={{ minWidth: 0 }}>
          <span className="exp-choice-label" style={{ display: 'block' }}>{label}</span>
          {sub && <span className="exp-choice-sub" style={{ display: 'block' }}>{sub}</span>}
        </span>
        {active && <span className="exp-choice-check"><Check size={16} strokeWidth={3} /></span>}
      </button>
      {/* El control que depende de la opcion vive DENTRO de ella, no suelto mas abajo. */}
      {active && children && <div className="exp-nested">{children}</div>}
    </div>
  );
}

function Step({ num, title, note, children }) {
  return (
    <div className="exp-step">
      <div className="exp-step-head">
        <span className="exp-step-num">{num}</span>
        <span className="exp-step-title">{title}</span>
        {note && <span className="exp-step-note">{note}</span>}
      </div>
      {children}
    </div>
  );
}

// Semana lunes-domingo que contiene `base`. Es la semana con la que trabaja todo el sistema.
const mondayOf = (base) => {
  const d = new Date(base);
  d.setHours(12, 0, 0, 0);
  const dow = d.getDay();
  d.setDate(d.getDate() + (dow === 0 ? -6 : 1 - dow));
  return d;
};
const toISO = (d) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${dd}`;
};
const prettyDate = (iso) => {
  if (!iso) return '';
  const [y, m, d] = iso.split('-');
  return `${Number(m)}/${Number(d)}/${y.slice(2)}`;
};

// Aviso de punches capturados a mano de la persona elegida. No bloquea nada: es informacion para
// decidir si el reporte se revisa antes de mandarlo, porque esas horas no las leyo una puerta.
function ManualPunches({ loading, info, t }) {
  if (loading) {
    return (
      <div style={{ marginTop: 8, fontSize: 12, color: 'var(--text-muted)' }}>
        {t('export.manualChecking')}
      </div>
    );
  }
  if (!info) return null;
  if (info.total === 0) {
    return (
      <div style={{ marginTop: 8, fontSize: 12, color: 'var(--text-muted)', display: 'flex', alignItems: 'center', gap: 6 }}>
        <Check size={13} strokeWidth={2.5} />
        {t('export.manualNone')}
      </div>
    );
  }
  // Solo se listan los primeros; el resto se resume. Un mes con muchas correcciones no debe empujar
  // el boton de exportar fuera de la pantalla.
  const shown = info.rows.slice(0, 4);
  const rest = info.total - shown.length;
  return (
    <div style={{
      marginTop: 10, padding: '10px 12px', borderRadius: 'var(--radius)',
      background: 'rgba(245,158,11,0.10)', border: '1px solid rgba(245,158,11,0.35)',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 7, color: 'var(--warning)', fontWeight: 700, fontSize: 12.5 }}>
        <AlertTriangle size={14} strokeWidth={2.4} />
        {info.total} {t('export.manualFound')}
      </div>
      <div style={{ marginTop: 6, display: 'flex', flexDirection: 'column', gap: 3 }}>
        {shown.map(r => (
          <div key={r.id_ts} style={{ fontSize: 11.5, color: 'var(--text-secondary)', fontVariantNumeric: 'tabular-nums' }}>
            {prettyDate(String(r.date_ts).substring(0, 10))} · {String(r.hour_ts).substring(0, 5)}
            {r.action_ts ? ` · ${r.action_ts}` : ''}
            {r.door_name_ts ? ` · ${r.door_name_ts}` : ''}
          </div>
        ))}
        {rest > 0 && (
          <div style={{ fontSize: 11.5, color: 'var(--text-muted)' }}>
            {t('export.manualMore').replace('{n}', rest)}
          </div>
        )}
      </div>
      <div style={{ marginTop: 7, fontSize: 11, color: 'var(--text-muted)', lineHeight: 1.4 }}>
        {t('export.manualWhy')}
      </div>
    </div>
  );
}

// `showDateRange`: el modal decide su propio periodo (Historial). Los valores del filtro de la
// pantalla solo SIEMBRAN los campos al abrir; a partir de ahi mandan estos, y mover el filtro de
// atras ya no cambia lo que se exporta.
// Reportes NO lo activa a proposito: alli la semana la dicta el archivo importado y el calculo que
// se acaba de correr, asi que dejar cambiarla aqui exportaria una semana distinta a la del preview.
export default function ExportFilterModal({
  isOpen, onClose, exportType, loading, onExport, defaultFacility,
  showDateRange = false, defaultDateFrom = '', defaultDateTo = '',
}) {
  const { t } = useLanguage();
  const [facility, setFacility] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [filterMode, setFilterMode] = useState('all');
  const [employeeType, setEmployeeType] = useState('PERM');
  const [badgeNumber, setBadgeNumber] = useState('');
  const [reportVariant, setReportVariant] = useState('detailed');
  const [format, setFormat] = useState('pdf');
  const [company, setCompany] = useState('');
  const [companies, setCompanies] = useState([]);

  // Employee search state
  const [empSearch, setEmpSearch] = useState('');
  const [empResults, setEmpResults] = useState([]);
  const [empSelected, setEmpSelected] = useState(null);
  const [showDropdown, setShowDropdown] = useState(false);
  const searchTimeout = useRef(null);
  const dropdownRef = useRef(null);
  // La ultima facilidad que el usuario eligio A MANO. Sirve para devolversela al salir de "una sola
  // persona": sin esto, elegir a alguien y luego volver a "todos" le borraba su eleccion.
  const pickedFacility = useRef('');

  // Punches capturados a mano de la persona elegida, dentro del periodo del reporte.
  const [manualInfo, setManualInfo] = useState(null);
  const [manualLoading, setManualLoading] = useState(false);

  // Reset state when modal opens
  useEffect(() => {
    if (isOpen) {
      setFacility(defaultFacility || '');
      setDateFrom(defaultDateFrom || '');
      setDateTo(defaultDateTo || '');
      setFilterMode('all');
      setEmployeeType('PERM');
      setBadgeNumber('');
      setReportVariant('detailed');
      setFormat('pdf');
      setCompany('');
      setEmpSearch('');
      setEmpResults([]);
      setEmpSelected(null);
      setShowDropdown(false);
      setManualInfo(null);
      pickedFacility.current = defaultFacility || '';
    }
  }, [isOpen, defaultFacility, defaultDateFrom, defaultDateTo]);

  // Load distinct companies for the selected facility
  useEffect(() => {
    if (!isOpen || !facility) { setCompanies([]); return; }
    let cancelled = false;
    axios.get('/api/employees/companies', { params: { facility } })
      .then(res => { if (!cancelled) setCompanies(Array.isArray(res.data) ? res.data : []); })
      .catch(() => { if (!cancelled) setCompanies([]); });
    return () => { cancelled = true; };
  }, [isOpen, facility]);

  // Close dropdown on outside click
  useEffect(() => {
    const handleClick = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const searchEmployees = useCallback(async (query) => {
    if (!query || query.length < 1) {
      setEmpResults([]);
      setShowDropdown(false);
      return;
    }
    try {
      const res = await axios.get('/api/employees', { params: { search: query, limit: 10 } });
      setEmpResults(res.data.data || []);
      setShowDropdown(true);
    } catch {
      setEmpResults([]);
    }
  }, []);

  const handleSearchChange = (val) => {
    setEmpSearch(val);
    setEmpSelected(null);
    setBadgeNumber('');
    if (searchTimeout.current) clearTimeout(searchTimeout.current);
    searchTimeout.current = setTimeout(() => searchEmployees(val), 300);
  };

  const handleSelectEmployee = (emp) => {
    setEmpSelected(emp);
    setBadgeNumber(String(emp.badge_num_emp));
    setEmpSearch(`${emp.name_emp} ${emp.last_name_emp} (${emp.badge_num_emp})`);
    setShowDropdown(false);
    // La facilidad la manda el EMPLEADO, no el filtro de la pantalla. El buscador no esta acotado a
    // una facilidad, asi que elegir a alguien de AUSWH con el filtro en MIA producia un reporte
    // vacio sin decir por que. La persona es mas especifica que el filtro: gana ella.
    if (emp.location_emp) setFacility(String(emp.location_emp));
  };

  // Al salir de "una sola persona", la facilidad vuelve a la de la pantalla: la del empleado solo
  // manda mientras el reporte sea de el.
  const changeFilterMode = (mode) => {
    setFilterMode(mode);
    if (mode !== 'badge') setFacility(pickedFacility.current || defaultFacility || '');
  };

  const pickFacility = (f) => {
    pickedFacility.current = f;
    setFacility(f);
  };

  // El periodo que de verdad cubre el reporte: el del modal cuando el modal lo manda (Historial), y
  // el que trae la pantalla cuando no (Reportes, donde la semana la dicta el archivo importado).
  const effFrom = showDateRange ? dateFrom : defaultDateFrom;
  const effTo = showDateRange ? dateTo : defaultDateTo;

  // Cuantos punches de esa persona los escribio un humano en vez del lector de puertas. Se pregunta
  // solo cuando el reporte es de UNA persona: es ahi donde el dato cabe en pantalla y donde importa
  // revisarlo antes de pagar. `pageSize` acotado porque de una persona en una semana son unos pocos.
  useEffect(() => {
    if (!isOpen || filterMode !== 'badge' || !empSelected || !effFrom || !effTo) {
      setManualInfo(null);
      setManualLoading(false);
      return;
    }
    let cancelled = false;
    setManualLoading(true);
    axios.get('/api/timestamps', {
      params: {
        badge: empSelected.badge_num_emp,
        facility: facility || undefined,
        dateFrom: effFrom,
        dateTo: effTo,
        onlyManual: 1,
        pageSize: 100,
      },
    })
      .then(res => {
        if (cancelled) return;
        setManualInfo({ total: Number(res.data.total) || 0, rows: res.data.data || [] });
      })
      .catch(() => { if (!cancelled) setManualInfo(null); })
      .finally(() => { if (!cancelled) setManualLoading(false); });
    return () => { cancelled = true; };
  }, [isOpen, filterMode, empSelected, facility, effFrom, effTo]);

  // Rango completo y en orden. Los inputs ya se corrigen entre si, esto es el cinturon por si el
  // modal abre sembrado con un rango a medias.
  const datesOk = !showDateRange || !!(dateFrom && dateTo && dateFrom <= dateTo);
  const employeeOk = filterMode !== 'badge' || !!empSelected;
  const canExport = !!facility && datesOk && employeeOk;

  const handleDateFromChange = (val) => {
    setDateFrom(val);
    if (dateTo && val && val > dateTo) setDateTo(val);
  };

  const handleDateToChange = (val) => {
    setDateTo(val);
    if (dateFrom && val && val < dateFrom) setDateFrom(val);
  };

  // Atajos a la semana lunes-domingo, que es la unidad con la que se calcula y se paga.
  const applyWeek = (weeksBack) => {
    const mon = mondayOf(new Date());
    mon.setDate(mon.getDate() - 7 * weeksBack);
    const sun = new Date(mon);
    sun.setDate(sun.getDate() + 6);
    setDateFrom(toISO(mon));
    setDateTo(toISO(sun));
  };

  const handleExport = () => {
    if (!canExport) return;
    onExport({
      facility,
      // Solo viajan cuando el modal es el dueno del periodo; si no, la pagina usa el suyo.
      dateFrom: showDateRange ? dateFrom : undefined,
      dateTo: showDateRange ? dateTo : undefined,
      filterMode,
      employeeType: filterMode === 'type' ? employeeType : undefined,
      badgeNumber: filterMode === 'badge' ? badgeNumber.trim() : undefined,
      // El filtro por compania aplica sobre "todos" o "por tipo" (no sobre un empleado especifico)
      company: filterMode !== 'badge' && company ? company : undefined,
      reportVariant: exportType === 'payroll' ? reportVariant : undefined,
      format,
    });
  };

  const title = exportType === 'timecards' ? t('export.titleTimecards') : t('export.titlePayroll');

  // Los pasos se numeran solos: el periodo existe en Historial pero no en Reportes, y el tipo de
  // reporte solo en payroll. Un contador evita numeros saltados.
  let step = 0;
  const next = () => ++step;

  // Cuando el reporte es de una sola persona la facilidad se DERIVA de ella y deja de ser una
  // eleccion: mostrarla como pregunta invitaba a contestarla mal.
  const empFacility = filterMode === 'badge' && empSelected ? String(empSelected.location_emp || '') : '';
  const facilityFromEmployee = !!empFacility;
  const lockedFacility = facilityFromEmployee ? empFacility : (defaultFacility || '');

  // Que empleados cubre, en una frase — lo mismo que el usuario acaba de elegir arriba.
  const scopeLabel = filterMode === 'badge'
    ? (empSelected ? `${empSelected.name_emp} ${empSelected.last_name_emp}` : t('export.byEmployee'))
    : filterMode === 'type' ? employeeType
    : (company || t('export.allEmployees'));

  // En el mismo orden que los pasos, para que senale el primero que falta y no uno de mas abajo.
  const missing = !employeeOk ? t('export.pendingEmployee')
    : !facility ? t('export.pendingFacility')
    : !datesOk ? t('export.pendingDates')
    : null;

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={title} large>
      <div className="modal-body">
        <div className="exp-steps">

          {/* ===== Empleados ===== */}
          {/* Primero QUIEN. Cuando el reporte es de una sola persona, la facilidad se deriva de
              ella: preguntarla antes era pedir una respuesta que el paso siguiente sobreescribia. */}
          <Step num={next()} title={t('export.filterLabel')}>
            <div className="exp-choices">
              <Choice active={filterMode === 'all'} onClick={() => changeFilterMode('all')}
                icon={<Users size={20} strokeWidth={1.8} />}
                label={t('export.allEmployees')} sub={t('export.allEmployeesSub')} />

              <Choice active={filterMode === 'type'} onClick={() => changeFilterMode('type')}
                icon={<Layers size={20} strokeWidth={1.8} />}
                label={t('export.byType')} sub={t('export.byTypeSub')}>
                <select className="form-input" style={{ maxWidth: 260, padding: '7px 10px', fontSize: 13 }}
                  value={employeeType} onChange={e => setEmployeeType(e.target.value)}>
                  <option value="PERM">PERM</option>
                  <option value="PERM-HOUR">PERM-HOUR</option>
                  <option value="PERM-SALARY">PERM-SALARY</option>
                  <option value="TEMP">TEMP</option>
                  <option value="CONTR">CONTR</option>
                </select>
              </Choice>

              <Choice active={filterMode === 'badge'} onClick={() => changeFilterMode('badge')}
                icon={<User size={20} strokeWidth={1.8} />}
                label={t('export.byEmployee')} sub={t('export.byEmployeeSub')}>
                <div style={{ position: 'relative', maxWidth: 320 }} ref={dropdownRef}>
                  <input type="text" className="form-input" style={{ padding: '7px 10px', fontSize: 13 }}
                    placeholder={t('export.employeePlaceholder')}
                    value={empSearch} onChange={e => handleSearchChange(e.target.value)}
                    onFocus={() => { if (empResults.length > 0 && !empSelected) setShowDropdown(true); }}
                    onKeyDown={e => { if (e.key === 'Enter' && empSelected) handleExport(); }} />
                  {showDropdown && empResults.length > 0 && (
                    <div className="export-search-dropdown">
                      {empResults.map(emp => (
                        <div key={emp.id_emp} className="export-search-item" onClick={() => handleSelectEmployee(emp)}>
                          <span style={{ fontWeight: 700 }}>{emp.badge_num_emp}</span>
                          <span>{emp.name_emp} {emp.last_name_emp}</span>
                          <span className="badge badge-info" style={{ marginLeft: 'auto', fontSize: 10 }}>{emp.type_emp}</span>
                        </div>
                      ))}
                    </div>
                  )}
                  {empSelected && (
                    <div style={{ marginTop: 8, fontSize: 12, color: 'var(--success)', display: 'flex', alignItems: 'center', gap: 6 }}>
                      <Check size={14} strokeWidth={3} />
                      Badge {empSelected.badge_num_emp} — {empSelected.name_emp} {empSelected.last_name_emp} ({empSelected.type_emp})
                    </div>
                  )}
                  {empSelected && <ManualPunches loading={manualLoading} info={manualInfo} t={t} />}
                </div>
              </Choice>
            </div>
          </Step>

          {/* ===== Facilidad ===== */}
          <Step num={next()} title={t('export.facilityLabel')}
            note={facilityFromEmployee ? t('export.facilityFromEmployee') : undefined}>
            {lockedFacility ? (
              <div className="exp-choice is-active" style={{ cursor: 'default' }}>
                <span className="exp-choice-icon"><Building2 size={20} strokeWidth={1.8} /></span>
                <span className="exp-choice-label">{lockedFacility}</span>
              </div>
            ) : (
              <div className="exp-choices exp-choices-2">
                {['MIA', 'AUSWH'].map(f => (
                  <Choice key={f} active={facility === f} onClick={() => pickFacility(f)}
                    icon={<Building2 size={20} strokeWidth={1.8} />} label={f} />
                ))}
              </div>
            )}
            {/* El empleado gano sobre el filtro de la pantalla: vale la pena decirlo, o pareceria
                que el modal ignoro lo que estaba seleccionado atras. */}
            {facilityFromEmployee && defaultFacility && empFacility !== defaultFacility && (
              <div style={{ marginTop: 10, fontSize: 12, color: 'var(--warning)', fontWeight: 600 }}>
                {t('export.facilityMismatch')} ({defaultFacility} → {empFacility})
              </div>
            )}
            {!facility && !lockedFacility && (
              <div style={{ marginTop: 10, fontSize: 12, color: 'var(--danger)', fontWeight: 600 }}>
                {t('export.facilityRequired')}
              </div>
            )}

            {/* La compania vive aqui, no en el paso de empleados: la lista sale de la facilidad, y
                con el orden nuevo alla quedaria un select apagado esperando algo de mas abajo.
                Afina "todos" o "por tipo"; sobre una persona concreta no aplica. */}
            {filterMode !== 'badge' && (
              <div style={{ marginTop: 14 }}>
                <label className="form-label" style={{ display: 'block', marginBottom: 6, fontSize: 12, color: 'var(--text-muted)' }}>
                  {t('export.companyLabel')}
                </label>
                <select className="form-input" style={{ maxWidth: 320, padding: '7px 10px', fontSize: 13 }}
                  value={company} onChange={e => setCompany(e.target.value)}
                  disabled={!facility || companies.length === 0}>
                  <option value="">{t('export.allCompanies')}</option>
                  {companies.map(c => (
                    <option key={c} value={c}>{c}</option>
                  ))}
                </select>
              </div>
            )}
          </Step>

          {/* ===== Periodo ===== */}
          {showDateRange && (
            <Step num={next()} title={t('export.sectionPeriod')} note={t('export.periodHint')}>
              <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'flex-end' }}>
                {/* El ancho va en el contenedor: DateInput esparce sus props sobre el `style` del
                    input y pasarle uno le borraria el espacio reservado al icono de calendario. */}
                <div style={{ width: 168 }}>
                  <label className="form-label" style={{ display: 'block', marginBottom: 6, fontSize: 12, color: 'var(--text-muted)' }}>
                    {t('common.from')}
                  </label>
                  <DateInput className="form-input"
                    value={dateFrom} max={dateTo || undefined} onChange={handleDateFromChange} />
                </div>
                <div style={{ width: 168 }}>
                  <label className="form-label" style={{ display: 'block', marginBottom: 6, fontSize: 12, color: 'var(--text-muted)' }}>
                    {t('common.to')}
                  </label>
                  <DateInput className="form-input"
                    value={dateTo} min={dateFrom || undefined} onChange={handleDateToChange} />
                </div>
                <div style={{ display: 'flex', gap: 8, paddingBottom: 9 }}>
                  <button type="button" className="exp-chip" onClick={() => applyWeek(0)}>
                    {t('export.thisWeek')}
                  </button>
                  <button type="button" className="exp-chip" onClick={() => applyWeek(1)}>
                    {t('export.lastWeek')}
                  </button>
                </div>
              </div>
              {!datesOk && (
                <div style={{ marginTop: 10, fontSize: 12, color: 'var(--danger)', fontWeight: 600 }}>
                  {t('export.periodRequired')}
                </div>
              )}
            </Step>
          )}

          {/* ===== Tipo de reporte (solo payroll) ===== */}
          {exportType === 'payroll' && (
            <Step num={next()} title={t('export.reportType')}>
              <div className="exp-choices">
                <Choice active={reportVariant === 'detailed'} onClick={() => setReportVariant('detailed')}
                  icon={<ClipboardList size={20} strokeWidth={1.8} />}
                  label={t('export.detailed')} sub={t('export.detailedSub')} />
                <Choice active={reportVariant === 'summary'} onClick={() => setReportVariant('summary')}
                  icon={<CalendarDays size={20} strokeWidth={1.8} />}
                  label={t('export.summary')} sub={t('export.summarySub')} />
              </div>
            </Step>
          )}

          {/* ===== Formato ===== */}
          <Step num={next()} title={t('export.format')}>
            <div className="exp-choices exp-choices-2">
              <Choice active={format === 'pdf'} onClick={() => setFormat('pdf')}
                icon={<FileText size={20} strokeWidth={1.8} />}
                label={t('export.pdf')} sub={t('export.pdfSub')} />
              <Choice active={format === 'excel'} onClick={() => setFormat('excel')}
                icon={<FileSpreadsheet size={20} strokeWidth={1.8} />}
                label={t('export.excel')} sub={t('export.excelSub')} />
            </div>
          </Step>

        </div>
      </div>

      {/* El resumen vive junto al boton: se ve exactamente que archivo va a salir antes de pedirlo. */}
      <div className="modal-footer" style={{ justifyContent: 'space-between', gap: 16, flexWrap: 'wrap' }}>
        <div className="exp-summary">
          {missing ? (
            <span className="exp-summary-warn">{missing}</span>
          ) : (
            <>
              <span style={{ color: 'var(--text-muted)' }}>{t('export.willDownload')}</span>
              <strong>{format === 'excel' ? t('export.excel') : t('export.pdf')}</strong>
              <span className="exp-summary-sep">·</span>
              <strong>{facility}</strong>
              <span className="exp-summary-sep">·</span>
              <span>{scopeLabel}</span>
              {showDateRange && (
                <>
                  <span className="exp-summary-sep">·</span>
                  <span>{prettyDate(dateFrom)} – {prettyDate(dateTo)}</span>
                </>
              )}
            </>
          )}
        </div>
        <div style={{ display: 'flex', gap: 8, flex: 'none' }}>
          <button className="btn-secondary" onClick={onClose}>{t('common.cancel')}</button>
          <button className="btn-primary" style={{ width: 'auto', padding: '8px 22px', display: 'flex', alignItems: 'center', gap: 7 }}
            onClick={handleExport}
            disabled={loading || !canExport}>
            <Download size={15} strokeWidth={2.2} />
            {loading ? t('common.exporting') : t('export.export')}
          </button>
        </div>
      </div>
    </Modal>
  );
}
